import React from 'react';
import { useTheme, ListItem } from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from "react-i18next";
import {
  AssetType,
  CameraData,
  getCameraFileAssets,
  getLandXMLAssets,
  getModelAssets,
  getOrthoAssets,
  getPointCloudAssets,
  getTagAssets,
  getWebMapAssets,
  selectAllAssets,
  TagData
} from "../redux/assets-slice";
import { ArrowTooltip } from '../components';
import { useSelector } from 'react-redux';

interface BulletListItemProps {
  children: React.ReactNode
}

const BulletListItem = (props: BulletListItemProps) => {
  const theme = useTheme();

  return (
    <ListItem style={{
      display: 'list-item',
      padding: theme.spacing(0.5, 1)
    }}>
      {props.children}
    </ListItem>
  );
};

export const NewProjectInfo = () => {
  const assets = useSelector(selectAllAssets);
  const orthoFiles = getOrthoAssets(assets);
  const pointClouds = getPointCloudAssets(assets);
  const cameraFiles = getCameraFileAssets(assets);
  const modelFiles = getModelAssets(assets);
  const landXMLFiles = getLandXMLAssets(assets);
  const webmapSources = getWebMapAssets(assets);
  const tagFiles = getTagAssets(assets);

  const theme = useTheme();
  const {t} = useTranslation();

  const numGenericPointCloud = pointClouds.filter(asset => {
    return (asset.type === AssetType.LAS)
      || (asset.type === AssetType.E57Points);
  }).length;

  let numberOfImages = 0;
  cameraFiles.forEach(cameraAsset => {
    const images = (cameraAsset.data as CameraData[]);
    numberOfImages += images.length;
  });

  let numberOfTags = 0;
  tagFiles.forEach(tagAsset => {
    const tags = (tagAsset.data as TagData).items;
    numberOfTags += tags.length;
  });

  return (
    <React.Fragment>
      {(pointClouds.length > 0) && (
        <BulletListItem>
          {t("export.asset-points", {
            count: pointClouds.length
          })}

          {(numGenericPointCloud > 0) && (
            <ArrowTooltip title={t('export.this-process-may-take-some-time')}>
              <WarningIcon style={{
                color: theme.palette.warning.main,
                marginBottom: "-5px",
                paddingLeft: "5px"
              }} fontSize="small"/>
            </ArrowTooltip>
          )}
        </BulletListItem>
      )}

      {(numberOfImages > 0) && (
        <BulletListItem>
          {t("export.asset-camera", {
            count: numberOfImages
          })}
        </BulletListItem>
      )}

      {(numberOfTags > 0) && (
        <BulletListItem>
          {t("export.asset-tags", {
            count: numberOfTags
          })}
        </BulletListItem>
      )}

      {(orthoFiles.length > 0) && (
        <BulletListItem>
          {t("export.asset-orthos", {
            count: orthoFiles.length
          })}
        </BulletListItem>
      )}

      {(modelFiles.length > 0) && (
        <BulletListItem>
          {t("export.asset-models", {
            count: modelFiles.length
          })}
        </BulletListItem>
      )}

      {(webmapSources.length > 0) && (
        <BulletListItem>
          {t("export.asset-webmap", {
            count: webmapSources.length
          })}
        </BulletListItem>
      )}

      {(landXMLFiles.length > 0) && (
        <BulletListItem>
          {t("export.asset-landxml", {
            count: landXMLFiles.length
          })}
        </BulletListItem>
      )}

    </React.Fragment>
  );
};
